import type {
  AiDiagnosticSource,
  AiDiagnosticStatus,
} from "./aiDiagnostics.ts";

export interface AiDiagnosticAggregationRow {
  id: string;
  parentDiagnosticId: string | null;
  workflow: string;
  status: AiDiagnosticStatus;
  source: AiDiagnosticSource;
  model: string;
  startedAt: number;
  durationMs: number | null;
  systemLength: number;
  promptLength: number;
  inputTokens: number | null;
  cachedInputTokens: number | null;
  outputTokens: number | null;
  reasoningTokens: number | null;
  totalTokens: number | null;
  estimatedCostUsd: number | null;
  pricingKnown: boolean;
}

export interface AiDiagnosticUsageBreakdown {
  key: string;
  calls: number;
  totalTokens: number;
  knownCostUsd: number;
  unknownCostCallCount: number;
}

export interface AiWorkflowUsageRollup {
  workflow: string;
  calls: number;
  successCount: number;
  errorCount: number;
  pendingCount: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  knownCostUsd: number;
  unknownCostCallCount: number;
  averageTokensPerCall: number | null;
  maxTokensPerCall: number | null;
  averagePromptCharacters: number;
  maxPromptCharacters: number;
  averageDurationMs: number | null;
  maxDurationMs: number | null;
  logicalOperationCount: number;
  logicalOperationTotalTokens: number;
  logicalOperationKnownCostUsd: number;
  logicalOperationUnknownCostCount: number;
  averageTokensPerLogicalOperation: number | null;
  maxTokensPerLogicalOperation: number | null;
  models: AiDiagnosticUsageBreakdown[];
  sources: AiDiagnosticUsageBreakdown[];
}

export interface AiDiagnosticAggregation {
  workflows: AiWorkflowUsageRollup[];
}

export interface AiDiagnosticAggregationResult extends AiDiagnosticAggregation {
  startTs: number;
  endTs: number;
  scannedRowCount: number;
  includedRowCount: number;
  maxScannedRowCount: number;
  truncated: boolean;
}

type UsageAccumulator = {
  calls: number;
  successCount: number;
  errorCount: number;
  pendingCount: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  knownTokenCallCount: number;
  maxTokensPerCall: number | null;
  knownCostUsd: number;
  unknownCostCallCount: number;
  promptCharacters: number;
  maxPromptCharacters: number;
  durationMs: number;
  durationCount: number;
  maxDurationMs: number | null;
  models: Map<string, AiDiagnosticUsageBreakdown>;
  sources: Map<string, AiDiagnosticUsageBreakdown>;
};

type LogicalOperationAccumulator = {
  workflow: string;
  totalTokens: number;
  hasKnownTokens: boolean;
  knownCostUsd: number;
  hasUnknownCost: boolean;
};

export function aggregateAiDiagnostics(
  rows: readonly AiDiagnosticAggregationRow[],
): AiDiagnosticAggregation {
  const workflowUsage = new Map<string, UsageAccumulator>();
  const rowsById = new Map(rows.map((row) => [row.id, row]));
  const operations = new Map<string, LogicalOperationAccumulator>();

  for (const row of rows) {
    const usage = workflowUsage.get(row.workflow) ?? createUsageAccumulator();
    addCall(usage, row);
    workflowUsage.set(row.workflow, usage);

    const rootId = resolveRootId(row, rowsById);
    const root = rowsById.get(rootId);
    const operation = operations.get(rootId) ?? {
      workflow: root?.workflow ?? row.workflow,
      totalTokens: 0,
      hasKnownTokens: false,
      knownCostUsd: 0,
      hasUnknownCost: false,
    };
    const totalTokens = resolveTotalTokens(row);
    if (totalTokens !== null) {
      operation.totalTokens += totalTokens;
      operation.hasKnownTokens = true;
    }
    if (row.estimatedCostUsd === null) operation.hasUnknownCost = true;
    else operation.knownCostUsd += row.estimatedCostUsd;
    operations.set(rootId, operation);
  }

  const operationUsage = new Map<
    string,
    {
      count: number;
      totalTokens: number;
      knownTokenCount: number;
      maxTokens: number | null;
      knownCostUsd: number;
      unknownCostCount: number;
    }
  >();
  for (const operation of operations.values()) {
    const usage = operationUsage.get(operation.workflow) ?? {
      count: 0,
      totalTokens: 0,
      knownTokenCount: 0,
      maxTokens: null,
      knownCostUsd: 0,
      unknownCostCount: 0,
    };
    usage.count += 1;
    if (operation.hasKnownTokens) {
      usage.totalTokens += operation.totalTokens;
      usage.knownTokenCount += 1;
      usage.maxTokens = Math.max(usage.maxTokens ?? 0, operation.totalTokens);
    }
    usage.knownCostUsd += operation.knownCostUsd;
    usage.unknownCostCount += operation.hasUnknownCost ? 1 : 0;
    operationUsage.set(operation.workflow, usage);
  }

  const workflows = [...workflowUsage.entries()].map(([workflow, usage]) => {
    const logical = operationUsage.get(workflow);
    return {
      workflow,
      calls: usage.calls,
      successCount: usage.successCount,
      errorCount: usage.errorCount,
      pendingCount: usage.pendingCount,
      inputTokens: usage.inputTokens,
      cachedInputTokens: usage.cachedInputTokens,
      outputTokens: usage.outputTokens,
      reasoningTokens: usage.reasoningTokens,
      totalTokens: usage.totalTokens,
      knownCostUsd: usage.knownCostUsd,
      unknownCostCallCount: usage.unknownCostCallCount,
      averageTokensPerCall: average(usage.totalTokens, usage.knownTokenCallCount),
      maxTokensPerCall: usage.maxTokensPerCall,
      averagePromptCharacters: average(usage.promptCharacters, usage.calls) ?? 0,
      maxPromptCharacters: usage.maxPromptCharacters,
      averageDurationMs: average(usage.durationMs, usage.durationCount),
      maxDurationMs: usage.maxDurationMs,
      logicalOperationCount: logical?.count ?? 0,
      logicalOperationTotalTokens: logical?.totalTokens ?? 0,
      logicalOperationKnownCostUsd: logical?.knownCostUsd ?? 0,
      logicalOperationUnknownCostCount: logical?.unknownCostCount ?? 0,
      averageTokensPerLogicalOperation: logical
        ? average(logical.totalTokens, logical.knownTokenCount)
        : null,
      maxTokensPerLogicalOperation: logical?.maxTokens ?? null,
      models: sortBreakdowns(usage.models),
      sources: sortBreakdowns(usage.sources),
    } satisfies AiWorkflowUsageRollup;
  });

  workflows.sort((a, b) => b.totalTokens - a.totalTokens || b.calls - a.calls);
  return { workflows };
}

function createUsageAccumulator(): UsageAccumulator {
  return {
    calls: 0,
    successCount: 0,
    errorCount: 0,
    pendingCount: 0,
    inputTokens: 0,
    cachedInputTokens: 0,
    outputTokens: 0,
    reasoningTokens: 0,
    totalTokens: 0,
    knownTokenCallCount: 0,
    maxTokensPerCall: null,
    knownCostUsd: 0,
    unknownCostCallCount: 0,
    promptCharacters: 0,
    maxPromptCharacters: 0,
    durationMs: 0,
    durationCount: 0,
    maxDurationMs: null,
    models: new Map(),
    sources: new Map(),
  };
}

function addCall(usage: UsageAccumulator, row: AiDiagnosticAggregationRow): void {
  usage.calls += 1;
  usage.successCount += row.status === "success" ? 1 : 0;
  usage.errorCount += row.status === "error" ? 1 : 0;
  usage.pendingCount += row.status === "pending" ? 1 : 0;
  usage.inputTokens += row.inputTokens ?? 0;
  usage.cachedInputTokens += row.cachedInputTokens ?? 0;
  usage.outputTokens += row.outputTokens ?? 0;
  usage.reasoningTokens += row.reasoningTokens ?? 0;
  const totalTokens = resolveTotalTokens(row);
  if (totalTokens !== null) {
    usage.totalTokens += totalTokens;
    usage.knownTokenCallCount += 1;
    usage.maxTokensPerCall = Math.max(usage.maxTokensPerCall ?? 0, totalTokens);
  }
  if (row.estimatedCostUsd === null) usage.unknownCostCallCount += 1;
  else usage.knownCostUsd += row.estimatedCostUsd;
  const promptCharacters = row.systemLength + row.promptLength;
  usage.promptCharacters += promptCharacters;
  usage.maxPromptCharacters = Math.max(usage.maxPromptCharacters, promptCharacters);
  if (row.durationMs !== null) {
    usage.durationMs += row.durationMs;
    usage.durationCount += 1;
    usage.maxDurationMs = Math.max(usage.maxDurationMs ?? 0, row.durationMs);
  }
  addBreakdown(usage.models, row.model, totalTokens, row.estimatedCostUsd);
  addBreakdown(usage.sources, row.source, totalTokens, row.estimatedCostUsd);
}

function addBreakdown(
  breakdowns: Map<string, AiDiagnosticUsageBreakdown>,
  key: string,
  totalTokens: number | null,
  estimatedCostUsd: number | null,
): void {
  const breakdown = breakdowns.get(key) ?? {
    key,
    calls: 0,
    totalTokens: 0,
    knownCostUsd: 0,
    unknownCostCallCount: 0,
  };
  breakdown.calls += 1;
  breakdown.totalTokens += totalTokens ?? 0;
  if (estimatedCostUsd === null) breakdown.unknownCostCallCount += 1;
  else breakdown.knownCostUsd += estimatedCostUsd;
  breakdowns.set(key, breakdown);
}

function resolveRootId(
  row: AiDiagnosticAggregationRow,
  rowsById: ReadonlyMap<string, AiDiagnosticAggregationRow>,
): string {
  let current = row;
  const visited = new Set<string>([row.id]);
  while (current.parentDiagnosticId) {
    if (visited.has(current.parentDiagnosticId)) break;
    const parent = rowsById.get(current.parentDiagnosticId);
    if (!parent) return current.parentDiagnosticId;
    visited.add(parent.id);
    current = parent;
  }
  return current.id;
}

function resolveTotalTokens(row: AiDiagnosticAggregationRow): number | null {
  if (row.totalTokens !== null) return row.totalTokens;
  if (row.inputTokens === null && row.outputTokens === null) return null;
  return (row.inputTokens ?? 0) + (row.outputTokens ?? 0);
}

function average(total: number, count: number): number | null {
  return count > 0 ? total / count : null;
}

function sortBreakdowns(
  breakdowns: ReadonlyMap<string, AiDiagnosticUsageBreakdown>,
): AiDiagnosticUsageBreakdown[] {
  return [...breakdowns.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || b.calls - a.calls || a.key.localeCompare(b.key),
  );
}
